import React, { useState } from 'react';
import { connect } from 'react-redux';
import {
  Modal,
  TextField,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Snackbar,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import axios from 'axios';
import { SubmitButton } from '../common/forms/buttons';

const useStyles = makeStyles((theme) => ({
  modalBody: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 500,
    maxHeight: '80vh',
    overflow: 'auto',
    outline: 'none',
    border: 'none',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 4,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(3, 4, 4),
  },
  title: {
    marginBottom: theme.spacing(3),
    color: theme.palette.primary.main,
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  error: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
  },
  submitButton: {
    marginTop: theme.spacing(3),
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  cancelButton: {
    marginTop: theme.spacing(3),
    marginRight: theme.spacing(2),
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 500,
  },
  releaseInput: {
    marginBottom: theme.spacing(2),
  },
  releaseList: {
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    maxHeight: 200,
    overflow: 'auto',
  },
  addReleaseButton: {
    marginTop: theme.spacing(1),
  },
  helpText: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(1),
  },
}));

function GuidedPilotSignupModal(props) {
  const { baseApiUri, jwtToken, open, onClose, onSuccess } = props;
  const classes = useStyles();

  // Form state
  const [companyName, setCompanyName] = useState('');
  const [projectName, setProjectName] = useState('');
  const [currentRelease, setCurrentRelease] = useState('');
  const [releases, setReleases] = useState([]);

  // Validation state
  const [companyError, setCompanyError] = useState('');
  const [projectError, setProjectError] = useState('');
  const [releaseError, setReleaseError] = useState('');
  const [generalError, setGeneralError] = useState('');

  // Loading and feedback state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidatingCompany, setIsValidatingCompany] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);

  // Validate company name uniqueness
  const validateCompanyName = async (name) => {
    if (!name) {
      setCompanyError('Company name is required');
      return false;
    }

    setIsValidatingCompany(true);
    setCompanyError('');

    try {
      const headers = { Authorization: jwtToken };
      const params = { name: name.trim() };
      const response = await axios.get(`${baseApiUri}/companies/name-available`, {
        headers,
        params
      });

      if (response.data.available === false) {
        setCompanyError('Company name already registered');
        setIsValidatingCompany(false);
        return false;
      }

      setCompanyError('');
      setIsValidatingCompany(false);
      return true;
    } catch (error) {
      setCompanyError('Error validating company name');
      setIsValidatingCompany(false);
      return false;
    }
  };

  // Add release to list
  const handleAddRelease = () => {
    if (!currentRelease.trim()) {
      setReleaseError('Release name cannot be empty');
      return;
    }

    if (releases.includes(currentRelease.trim())) {
      setReleaseError('This release has already been added');
      return;
    }

    setReleases([...releases, currentRelease.trim()]);
    setCurrentRelease('');
    setReleaseError('');
  };

  // Remove release from list
  const handleRemoveRelease = (releaseToRemove) => {
    setReleases(releases.filter(release => release !== releaseToRemove));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError('');

    // Validate required fields
    if (!companyName) {
      setCompanyError('Company name is required');
      return;
    }

    if (!projectName) {
      setProjectError('Project name is required');
      return;
    }

    // Validate company name uniqueness
    const isCompanyValid = await validateCompanyName(companyName);
    if (!isCompanyValid) {
      return;
    }

    setIsSubmitting(true);

    try {
      const headers = { Authorization: jwtToken };
      const body = {
        company: companyName.trim(),
        project: projectName.trim(),
        releases: releases.join(','), // Convert array to comma-separated string
      };

      const response = await axios.post(
        `${baseApiUri}/companies/trial`,
        body,
        { headers }
      );

      // Format the expiration date
      const expirationDate = response.data.membership_expires;
      const formattedDate = new Date(expirationDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

      setSuccessMessage(
        `Guided Pilot created successfully! Your pilot expires on ${formattedDate}. A welcome email has been sent to your registered email address.`
      );
      setShowSuccessSnackbar(true);

      // Reset form
      setCompanyName('');
      setProjectName('');
      setReleases([]);
      setCurrentRelease('');

      // Call success callback if provided
      if (onSuccess) {
        onSuccess(response.data);
      }

      // Close modal after a delay to show success message
      setTimeout(() => {
        onClose();
        setShowSuccessSnackbar(false);
      }, 3000);

    } catch (error) {
      console.error('Error creating Guided Pilot:', error);

      if (error.response?.status === 409) {
        setCompanyError('This company name is already taken');
      } else if (error.response?.data?.message) {
        setGeneralError(error.response.data.message);
      } else {
        setGeneralError('Failed to create Guided Pilot. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form when modal closes
  const handleClose = () => {
    if (!isSubmitting) {
      setCompanyName('');
      setProjectName('');
      setReleases([]);
      setCurrentRelease('');
      setCompanyError('');
      setProjectError('');
      setReleaseError('');
      setGeneralError('');
      onClose();
    }
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <div className={classes.modalBody}>
          <Typography variant="h5" className={classes.title}>
            Start Your Guided Pilot
          </Typography>

          <Typography className={classes.helpText}>
            Create your company account with a 4-week Guided Pilot. You'll be set as the company manager.
          </Typography>

          <form onSubmit={handleSubmit}>
            {/* Company Name */}
            <TextField
              fullWidth
              required
              label="Company Name"
              value={companyName}
              onChange={(e) => {
                setCompanyName(e.target.value);
                setCompanyError('');
              }}
              onBlur={() => companyName && validateCompanyName(companyName)}
              error={Boolean(companyError)}
              helperText={companyError || (isValidatingCompany ? 'Checking availability...' : '')}
              className={classes.field}
              variant="outlined"
              disabled={isSubmitting}
            />

            {/* Project Name */}
            <TextField
              fullWidth
              required
              label="Project Name"
              value={projectName}
              onChange={(e) => {
                setProjectName(e.target.value);
                setProjectError('');
              }}
              error={Boolean(projectError)}
              helperText={projectError}
              className={classes.field}
              variant="outlined"
              disabled={isSubmitting}
            />

            {/* Releases Section */}
            <Typography className={classes.sectionTitle}>
              Releases (Optional)
            </Typography>
            <Typography className={classes.helpText}>
              Add release versions for your project (e.g., v1.0, v2.0)
            </Typography>

            <div className={classes.releaseInput}>
              <TextField
                fullWidth
                label="Release Name"
                value={currentRelease}
                onChange={(e) => {
                  setCurrentRelease(e.target.value);
                  setReleaseError('');
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddRelease();
                  }
                }}
                error={Boolean(releaseError)}
                helperText={releaseError}
                variant="outlined"
                disabled={isSubmitting}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={handleAddRelease}
                      disabled={!currentRelease.trim() || isSubmitting}
                      edge="end"
                    >
                      <AddIcon />
                    </IconButton>
                  ),
                }}
              />
            </div>

            {/* Release List */}
            {releases.length > 0 && (
              <List className={classes.releaseList}>
                {releases.map((release, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={release} />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveRelease(release)}
                        disabled={isSubmitting}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}

            {/* General Error */}
            {generalError && (
              <Typography className={classes.error}>
                {generalError}
              </Typography>
            )}

            {/* Buttons */}
            <div className={classes.buttonContainer}>
              <Button
                className={classes.cancelButton}
                onClick={handleClose}
                disabled={isSubmitting}
                variant="outlined"
              >
                Cancel
              </Button>
              <SubmitButton
                className={classes.submitButton}
                text={isSubmitting ? 'Creating Guided Pilot...' : 'Start Guided Pilot'}
                disabled={isSubmitting || isValidatingCompany || !companyName || !projectName}
              />
            </div>
          </form>
        </div>
      </Modal>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccessSnackbar(false)}
          severity="success"
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

function mapStateToProps(state) {
  return {
    jwtToken: state.common.jwtToken,
    baseApiUri: state.common.baseApiUri,
  };
}

export default connect(mapStateToProps)(GuidedPilotSignupModal);